import { Button, Tooltip } from '@toggle/design-system';
import { DOW_JONES, SingleNews } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { WithFooterWrapper } from '~/components/with-footer-wrapper/WithFooterWrapper';
import { useNavigateToAssetOverview } from '~/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { TrackingMetadata } from '~/services/tracking/posthog/constants';

import * as S from './ArticleDrawer.styles';
import { SingleArticle } from './components/single-article/SingleArticle';
import * as Shared from './components/single-article/SingleArticle.styles';

interface ArticleDrawerContentProps {
  isError?: boolean;
  isFetching?: boolean;
  data?: SingleNews;
  onArticleClick?: (singleNews: SingleNews) => void;
  onClose?: () => void;
  list?: SingleNews[];
  newsId: string;
  trackingMetadata?: TrackingMetadata;
}

export const ArticleDrawerContent = ({
  isError = false,
  isFetching = false,
  data,
  onArticleClick,
  onClose,
  list = [],
  newsId,
  trackingMetadata,
}: ArticleDrawerContentProps) => {
  const { t } = useTranslation(['news', 'chart', 'analyze', 'countries']);
  const { navigateToPage } = useNavigateToAssetOverview();

  const newsIndex = list.findIndex(item => item.id === newsId);
  const nextArticle = list[newsIndex + 1];
  const prevArticle = list[newsIndex - 1];
  const hasFooter = !!nextArticle || !!prevArticle;

  const navigateToAssetOverview = (entityTag: string) => {
    onClose?.();
    navigateToPage({ entityTag });
  };

  const renderContent = () => {
    if (isFetching) {
      return <Shared.ContentSkeleton data-testid="loading-state" />;
    }

    if (isError && !isFetching) {
      return (
        <S.StyledErrorMessage
          data-testid="error-state"
          displayIcon
          variant="error"
        />
      );
    }

    if (!!data) {
      return (
        <SingleArticle
          article={data}
          isLoading={isFetching}
          onEntityClick={navigateToAssetOverview}
        />
      );
    }

    return null;
  };

  return (
    <WithFooterWrapper trackingMetadata={trackingMetadata}>
      <S.Content $hasFooter={hasFooter}>
        {renderContent()}
        {data?.source === DOW_JONES && (
          <S.DowJonesDisclaimer>
            {t('news:dowJonesDisclaimer.dowJonesCopyright', {
              year: new Date().getFullYear(),
            })}
          </S.DowJonesDisclaimer>
        )}
      </S.Content>
      {hasFooter && (
        <S.Footer data-testid="article-drawer-footer">
          <div>
            {!!prevArticle && onArticleClick && (
              <Tooltip label={prevArticle.title}>
                <Button
                  data-testid="prev-article"
                  label={t('news:drawer.prevArticle')}
                  variant="empty"
                  size="xsmall"
                  iconName="ArrowUp"
                  iconPosition="right"
                  onClick={() => onArticleClick(prevArticle)}
                />
              </Tooltip>
            )}
          </div>

          <div>
            {!!nextArticle && onArticleClick && (
              <Tooltip label={nextArticle.title}>
                <Button
                  data-testid="next-article"
                  label={t('news:drawer.nextArticle')}
                  variant="empty"
                  size="xsmall"
                  iconName="ArrowDown"
                  iconPosition="right"
                  onClick={() => onArticleClick(nextArticle)}
                />
              </Tooltip>
            )}
          </div>
        </S.Footer>
      )}
    </WithFooterWrapper>
  );
};
